import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationCancellationCode, NavigationEnd, Router } from '@angular/router';
import { LoginService } from '@ebw-monorepo/login';
import { ExternalActionService } from '@ebw-monorepo/map';
import { NavigationService } from '@ebw-monorepo/navigation';
import { environment } from 'apps/portale-saas/src/environments/environment';
import { take } from 'rxjs';
import { IToolbarAction } from '../../model/toolbar-action';
import { SocketService } from '../../services/socket/socket.service';
import { ToolbarService } from '../../services/toolbar.service';
import { PERMISSION_NAME, PermissionService, SidePanelService } from '@ebw-monorepo/utils';

@Component({
  selector: 'ebw-monorepo-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [
    trigger('scaleAnimation', [
      transition(':enter', [
        animate('150ms ease-in', keyframes([style({ transform: 'scale(0)', opacity: 0 }), style({ transform: 'scale(1)', opacity: 1 })]))
      ]),
      transition(':leave', [
        animate('150ms ease-out', keyframes([style({ transform: 'scale(1)', opacity: 1 }), style({ transform: 'scale(0)', opacity: 0 })]))
      ])
    ])
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  public actions: IToolbarAction[] = [];
  public isSidePanelOpened = false;
  public isWidePanelOpened = false;
  public isHalfPanelOpened = false;
  public activeAction;
  public activeBack = false;
  public logo: string;
  public sub = [];
  public canSeeNotifications: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _toolbarService: ToolbarService,
    private _cdr: ChangeDetectorRef,
    private navigation: NavigationService,
    private loginService: LoginService,
    private externalActionSrv: ExternalActionService,
    private socketService: SocketService,
    private _permissionService: PermissionService,
    private _sidePanelService: SidePanelService
  ) {
    const regexSecondaryOutlet = /\(([^:]+):([^\)]+)\)/;
    this._router.events.subscribe((event) => {
      // navigazione rifiutata da un guard (es. route sidebar con uno strumento già aperto): la voce di menu torna a riflettere l'url corrente
      if (event instanceof NavigationCancel && event.code === NavigationCancellationCode.GuardRejected) {
        const currentUrl = this._router.url;
        const currentMatch = currentUrl.match(regexSecondaryOutlet);
        this.activeAction = currentMatch ? currentMatch[2] : null;
        this.isSidePanelOpened = currentUrl.includes('sidebarAction');
      }
      if (event instanceof NavigationEnd) {
        const matchSecondaryOutlet = event.url.match(regexSecondaryOutlet);

        if (matchSecondaryOutlet) {
          this.activeAction = matchSecondaryOutlet[2];
        } else {
          this.activeAction = null;
        }

        this.isSidePanelOpened = event.url.includes('sidebarAction');
      }
    });
  }

  ngOnInit(): void {
    this._permissionService.decodePermissions(sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY));

    if (this._permissionService.checkPermission(PERMISSION_NAME.NOTIFICATION.NOTIFICATION)) {
      this.canSeeNotifications = true;
    }

    let basePathLogo = './assets/custom';
    const tenantName = this.loginService.getTenantName();
    const isSaas = environment.deployMode === 'saas';
    basePathLogo += isSaas ? `/${tenantName}` : '';
    const nomeLogoImg = 'logo_sidebar.png';
    this.logo = `${basePathLogo}/${nomeLogoImg}`;

    this.sub.push(
      this.externalActionSrv.getViewerManager().subscribe({
        next: (val) => {
          this.activeBack = val;
        }
      })
    );
    // Socket
    this.socketService.init();
  }

  ngAfterContentChecked() {
    this._toolbarService.toolbarConfiguration$.pipe(take(1)).subscribe((actions) => {
      this.actions = actions;

      if (this.activeAction) {
        const activeConfig = this.actions?.find((a) => a.id === this.activeAction);
        if (activeConfig) {
          const override = this._sidePanelService.wideOverride$.value;

          const halfOverride = this._sidePanelService.halfOverride$.value;

          if (halfOverride) {
            this.isWidePanelOpened = false;
            this.isHalfPanelOpened = true;
          } else {
            this.isHalfPanelOpened = false;
            this.isWidePanelOpened = override ?? activeConfig.wide === true;
          }
        }
      }

      this._cdr.detectChanges();
    });
  }

  public sidenavClose() {
    this.activeAction = null;
    this.isSidePanelOpened = false;
    this.isWidePanelOpened = false;
    this._sidePanelService.wideOverride$.next(null);

    setTimeout(() => {
      this._router.navigate([{ outlets: { sidebarAction: null } }]).then(() => this._router.navigate(['./'], { relativeTo: this._route }));
    }, 250);
  }

  public menuAction() {
    this.isSidePanelOpened = true;
    this._router.navigate(['/home/menu']);
  }

  public emitAction(action: IToolbarAction) {
    if (this.isWidePanelOpened && action.eventType !== 'panel') {
      this.sidenavClose();
      setTimeout(() => (this.activeAction = action.id), 300);
    } else {
      this.activeAction = action.id;
    }

    this._sidePanelService.wideOverride$.next(null);
    this.isWidePanelOpened = action.wide === true;
    this._toolbarService.emitAction({
      id: action.id,
      cb: () => {
        if (action.id != 'drawing-tool') {
          this.isSidePanelOpened = true;
        }
      }
    });
  }

  public sidenavBack() {
    this.activeBack = false;
    const history = this.navigation.getHistory();
    if (history.length > 1) {
      this.navigation.goBack();
      return;
    }
    // No meaningful history (e.g. after page refresh): navigate to parent outlet route
    const url = this._router.url;
    const outletMatch = url.match(/\(([^:]+):([^)]+)\)/);
    if (outletMatch) {
      const outlet = outletMatch[1];
      const segments = outletMatch[2].split('/');
      if (segments.length > 1) {
        this._router.navigate(['/home', { outlets: { [outlet]: segments.slice(0, -1) } }]);
        return;
      }
    }
    this.navigation.goBack();
  }

  ngOnDestroy() {
    this.sub.map((s) => s.unsubscribe());
    this.socketService.disconnect();
  }
}
